import axios from "axios";

import { httpClient } from "@/shared/services/http";
import isValidationFailureResponse from "@/shared/utils/comparison/isValidationFailureResponse";
import { getFilenameFromContentDisposition } from "@/shared/utils/download.utils";
import { ValidationFailureResponse } from "@/shared/interfaces/validation-error.interface";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";

interface UploadSuccess {
  success: true;
  testId: number;
  locales: LanguagesIsoEnum[];
  elementCount: number;
  availableLocales: LanguagesIsoEnum[];
}

type UploadResult = UploadSuccess | ValidationFailureResponse;

export class TestTranslationHttpService {
  /**
   * Fetches the partially-filled CSV template for translating a test into `locales` (one value
   * column per language) as a blob plus its server-suggested filename; the caller decides how to
   * save it. The admin fills the columns and re-uploads the same file via {@link uploadTranslation}.
   */
  static async downloadTemplate(
    testId: number,
    locales: LanguagesIsoEnum[],
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await httpClient.get<Blob>(`/api/tests/${testId}/translation-template`, {
      params: { locales: locales.join(",") },
      responseType: "blob",
    });

    const filename =
      getFilenameFromContentDisposition(response.headers["content-disposition"]) ??
      `${testId}-${locales.join("-")}-translation.csv`;
    return { blob: response.data, filename };
  }

  /**
   * Uploads a filled-in translation file covering `locales` — either the CSV on its own or a ZIP of
   * that CSV plus the image files its cells reference. Returns the result directly; on validation
   * failures (HTTP 400) the structured failure payload is returned instead of throwing.
   */
  static async uploadTranslation(opts: {
    testId: number;
    locales: LanguagesIsoEnum[];
    file: File;
    /**
     * Translated group headers keyed by locale. Required (one per uploaded language) for a test
     * whose questions share a group header; omit for any other test.
     */
    groupHeaderText?: Partial<Record<LanguagesIsoEnum, string>>;
  }): Promise<UploadResult> {
    const { testId, locales, file, groupHeaderText } = opts;

    const formData = new FormData();
    formData.append("locales", locales.join(","));
    formData.append("file", file);
    // Multipart fields are strings, so the per-locale headers travel as one JSON-encoded object.
    if (groupHeaderText && Object.keys(groupHeaderText).length > 0) {
      formData.append("groupHeaderText", JSON.stringify(groupHeaderText));
    }

    try {
      const { data } = await httpClient.post<UploadResult>(`/api/tests/${testId}/translations`, formData, {
        timeout: 300_000,
      });
      return data;
    } catch (error: unknown) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        isValidationFailureResponse(error.response.data)
      ) {
        return error.response.data;
      }
      throw error;
    }
  }
}
